// services/frontend/src/utils/fileValidation.ts
// T006: File validation utilities for batch upload
//
// SECURITY NOTE: This client-side validation is for UX only (defense-in-depth).
// Primary security validation occurs server-side in:
// - services/api/src/controllers/photo-controller.ts (MIME type, file size)
// - services/api/src/services/image-processing.ts (magic byte validation via Sharp)

export const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/tiff'];

/** HTML file input accept attribute value */
export const ACCEPTED_FILE_INPUT = 'image/jpeg,image/png,image/tiff';

// File size limit: 500MB (matches backend limit)
export const MAX_FILE_SIZE_MB = 500;
export const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024; // 500MB in bytes

export interface InvalidFile {
  file: File;
  reason: string;
}

export interface FileValidationResult {
  valid: File[];
  invalid: InvalidFile[];
}

/**
 * Check if a single file is a valid image file
 */
export function isValidImageFile(file: File): boolean {
  return ACCEPTED_MIME_TYPES.includes(file.type) && file.size <= MAX_FILE_SIZE;
}

/**
 * Get validation error reason for a file
 */
export function getValidationError(file: File): string | null {
  if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
    return `Invalid file type: ${file.type}. Only JPEG, PNG, and TIFF files are supported.`;
  }

  if (file.size > MAX_FILE_SIZE) {
    return `File size exceeds 500MB limit. File is ${(file.size / 1024 / 1024).toFixed(2)}MB.`;
  }

  return null;
}

/**
 * Filter a list of files into valid and invalid groups
 */
export function filterValidFiles(files: File[]): FileValidationResult {
  const valid: File[] = [];
  const invalid: InvalidFile[] = [];

  for (const file of files) {
    const error = getValidationError(file);

    if (error) {
      invalid.push({ file, reason: error });
    } else {
      valid.push(file);
    }
  }

  return { valid, invalid };
}

/**
 * Generate a clean photo title from a filename
 * Removes file extension and cleans up common patterns
 */
export function generatePhotoTitle(filename: string): string {
  // Remove extension
  let title = filename.replace(/\.[^.]+$/, '');

  // Replace underscores and dashes with spaces
  title = title.replace(/[_-]/g, ' ');

  // Clean up multiple spaces
  title = title.replace(/\s+/g, ' ').trim();

  return title;
}

/**
 * Get a summary message for invalid files
 */
export function getInvalidFilesSummary(invalid: InvalidFile[]): string {
  if (invalid.length === 0) return '';

  if (invalid.length === 1) {
    return `1 file was skipped: ${invalid[0].reason}`;
  }

  const typeErrors = invalid.filter(i => i.reason.includes('Invalid file type')).length;
  const sizeErrors = invalid.filter(i => i.reason.includes('exceeds')).length;

  const parts: string[] = [];
  if (typeErrors > 0) parts.push(`${typeErrors} unsupported file type${typeErrors > 1 ? 's' : ''}`);
  if (sizeErrors > 0) parts.push(`${sizeErrors} file${sizeErrors > 1 ? 's' : ''} too large`);

  return `${invalid.length} file${invalid.length > 1 ? 's' : ''} skipped: ${parts.join(', ')}`;
}
